import { AvailableSearchSources } from '@bettermode/common/gql/generated'
import { SearchNetworkSettings } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useUpdateNetworkSearchSettings } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

interface Props {
  searchSettings: SearchNetworkSettings
}

export const SearchSourcesCard = ({ searchSettings }: Props) => {
  const { mutate: updateNetworkSearchSettings, isLoading } =
    useUpdateNetworkSearchSettings()
  const { $t } = useI18n()

  const submit = (data: AvailableSearchSources) => {
    const { members, spaces } = data

    updateNetworkSearchSettings(
      {
        input: {
          availableSearchSources: {
            members,
            spaces,
          },
        },
      },
      {
        onSuccess: () => {
          toast({
            title: $t({
              defaultMessage: 'Search settings updated.',
              id: 'Admin.SearchSettings.Updated',
            }),
            status: 'success',
          })
        },
        onError: () => {
          toast({
            title: $t({
              defaultMessage: 'Unable to update search settings.',
              id: 'Admin.SearchSettings.FailedToUpdate',
            }),
            status: 'error',
          })
        },
      },
    )
  }

  const { members, spaces } = searchSettings.availableSearchSources

  return (
    <Form
      defaultValues={{
        members,
        spaces,
      }}
      onSubmit={submit}
    >
      {({ formState: { dirtyFields } }) => (
        <Card>
          <Card.Header
            title={
              <T
                defaultMessage="Search Sources"
                id="Admin.Search.SearchSources.Title"
              />
            }
            description={
              <span>
                <T
                  defaultMessage="Admins have access to all sources. Select which content types should be available in search results for {MEMBERS}."
                  id="Admin.Search.SearchSources.Description"
                />
              </span>
            }
          />
          <Card.Content>
            <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
              <Form.Toggle
                name="posts"
                disabled
                label={$t({
                  defaultMessage: 'Posts',
                  id: 'Generics.Posts',
                })}
              />
              <Form.Toggle
                name="spaces"
                label={$t({
                  defaultMessage: '{SPACES_CC}',
                  id: 'Admin.Search.SearchSources.Spaces',
                })}
              />
              <Form.Toggle
                name="members"
                label={$t({
                  defaultMessage: '{MEMBERS_CC}',
                  id: 'Admin.Search.SearchSources.Members',
                })}
              />
            </div>
          </Card.Content>
          <Card.Actions>
            <Button
              type="submit"
              loading={isLoading}
              disabled={Object.keys(dirtyFields).length === 0 || isLoading}
            >
              <T defaultMessage="Update" id="Generics.Update" />
            </Button>
          </Card.Actions>
        </Card>
      )}
    </Form>
  )
}
